/**
 * PATH: apps/quote/src/lib/api/qdrive-files-api.ts
 * PURPOSE: List and fetch QDrive files using the Quote hub's own API client.
 * WHY: quote-builder ships the same calls, but they run on qbApi(), which this
 *      hub never primes (setQuoteBuilderApiClient is only called by the
 *      bravada/newmark shells). Injecting these into QDriveFilePicker keeps the
 *      shared picker UI while the request goes out on the hub's authed client.
 * FLOW: list(query) -> picker rows; download(file) -> File for the AI parser.
 */
import { getApiClient, withOrgConfig } from '../quote-api-helpers'

export interface QDriveFileSummary {
  id: string
  filename: string
  content_type?: string | null
  file_size?: number | null
  uploaded_at?: string | null
  folder_path?: string | null
}

function rowsOf(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[]
  const envelope = payload as { results?: unknown; files?: unknown } | null
  const nested = envelope?.results ?? envelope?.files
  return Array.isArray(nested) ? (nested as Record<string, unknown>[]) : []
}

function summaryOf(row: Record<string, unknown>): QDriveFileSummary | null {
  // Search hits nest the record under `file`; the list endpoint returns it flat.
  const src = (row.file && typeof row.file === 'object' ? row.file : row) as Record<string, unknown>
  const id = src.id ?? src.file_id
  const filename = src.filename ?? src.original_filename ?? src.name
  if (id == null || !filename) return null
  return {
    id: String(id),
    filename: String(filename),
    content_type: (src.content_type ?? src.mime_type ?? null) as string | null,
    file_size: typeof src.file_size === 'number' ? src.file_size : null,
    uploaded_at: (src.uploaded_at ?? src.created_at ?? null) as string | null,
    folder_path: (src.folder_path ?? src.path ?? null) as string | null,
  }
}

export const qdriveFilesApi = {
  /** Newest QDrive files, or the search endpoint when a query is typed. */
  async list(query?: string): Promise<QDriveFileSummary[]> {
    const client = getApiClient()
    const q = query?.trim()
    const res = q
      ? await client.get('/qdrive/search/', withOrgConfig({ params: { q } }))
      : await client.get(
          '/qdrive/files/',
          withOrgConfig({ params: { page_size: 50, ordering: '-uploaded_at' } }),
        )
    return rowsOf(res.data).map(summaryOf).filter(Boolean) as QDriveFileSummary[]
  },

  /**
   * Materialise a QDrive record as a File so it can go through the SAME
   * multipart parse endpoint a device upload uses. The download route answers
   * either with the bytes or with a JSON `{ url }` redirect to S3.
   */
  async download(file: QDriveFileSummary): Promise<File> {
    const client = getApiClient()
    const res = await client.get(
      `/qdrive/files/${file.id}/download/`,
      withOrgConfig({ responseType: 'blob' }),
    )
    let blob = res.data as Blob
    if (String(res.headers?.['content-type'] || '').includes('application/json')) {
      const payload = JSON.parse(await blob.text()) as { url?: string | null }
      if (!payload.url) throw new Error('QDrive did not return a download URL.')
      const signed = await fetch(payload.url)
      if (!signed.ok) throw new Error('Could not download the selected QDrive file.')
      blob = await signed.blob()
    }
    return new File([blob], file.filename, {
      type: blob.type || file.content_type || undefined,
    })
  },
}
